import React, { useState, useEffect } from 'react';
import { 
  Palette, 
  Type, 
  MessageSquare, 
  Sparkles,
  Save,
  RotateCcw,
  Eye
} from 'lucide-react';

export interface ChatbotTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: 'none' | 'small' | 'medium' | 'large';
  position: 'bottom-right' | 'bottom-left';
  welcomeMessage: string;
  companyName: string;
  showPoweredBy: boolean;
}

const defaultTheme: ChatbotTheme = {
  primaryColor: '#f97316', // orange-500
  secondaryColor: '#ea580c', // orange-600
  backgroundColor: '#18181b', // zinc-900
  textColor: '#fafafa', // zinc-50
  borderRadius: 'large',
  position: 'bottom-right',
  welcomeMessage: 'Cześć! 👋 W czym mogę pomóc?',
  companyName: 'PlexiSystem AI',
  showPoweredBy: true
};

export const ChatbotCustomization: React.FC = () => {
  const [theme, setTheme] = useState<ChatbotTheme>(defaultTheme);
  const [savedTheme, setSavedTheme] = useState<ChatbotTheme>(defaultTheme);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Załaduj zapisany motyw
    const saved = localStorage.getItem('chatbot_theme');
    if (saved) {
      const parsedTheme = JSON.parse(saved);
      setTheme(parsedTheme);
      setSavedTheme(parsedTheme);
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Zapisz w localStorage
      localStorage.setItem('chatbot_theme', JSON.stringify(theme));
      setSavedTheme(theme);
      
      // Tu możesz dodać zapis do bazy danych
      
      // Wyemituj event dla widgetu czatu
      window.dispatchEvent(new CustomEvent('chatbot-theme-updated', { detail: theme }));
      
      alert('Motyw został zapisany!');
    } catch (error) {
      console.error('Error saving theme:', error);
      alert('Błąd podczas zapisywania motywu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setTheme(defaultTheme);
  };

  const borderRadiusMap = {
    none: '0',
    small: '0.5rem',
    medium: '1rem',
    large: '1.5rem'
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Personalizacja Czatbota</h2>
        <p className="text-gray-600">Dostosuj wygląd czatbota do swojej marki</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Panel konfiguracji */}
        <div className="space-y-6">
          {/* Kolory */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Palette className="h-5 w-5 text-orange-500" />
              Kolory
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kolor główny
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={theme.primaryColor}
                    onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.primaryColor}
                    onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kolor dodatkowy
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={theme.secondaryColor}
                    onChange={(e) => setTheme({ ...theme, secondaryColor: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.secondaryColor}
                    onChange={(e) => setTheme({ ...theme, secondaryColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kolor tła
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={theme.backgroundColor}
                    onChange={(e) => setTheme({ ...theme, backgroundColor: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={theme.backgroundColor}
                    onChange={(e) => setTheme({ ...theme, backgroundColor: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Teksty */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Type className="h-5 w-5 text-orange-500" />
              Teksty
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa firmy
                </label>
                <input
                  type="text"
                  value={theme.companyName}
                  onChange={(e) => setTheme({ ...theme, companyName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wiadomość powitalna
                </label>
                <textarea
                  value={theme.welcomeMessage}
                  onChange={(e) => setTheme({ ...theme, welcomeMessage: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Ustawienia */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-orange-500" />
              Ustawienia
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zaokrąglenie rogów
                </label>
                <select
                  value={theme.borderRadius}
                  onChange={(e) => setTheme({ ...theme, borderRadius: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="none">Brak</option>
                  <option value="small">Małe</option>
                  <option value="medium">Średnie</option>
                  <option value="large">Duże</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pozycja na stronie
                </label>
                <select
                  value={theme.position}
                  onChange={(e) => setTheme({ ...theme, position: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="bottom-right">Prawy dolny róg</option>
                  <option value="bottom-left">Lewy dolny róg</option>
                </select>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Pokaż "Powered by Claude AI"
                </label>
                <button
                  type="button"
                  onClick={() => setTheme({ ...theme, showPoweredBy: !theme.showPoweredBy })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    theme.showPoweredBy ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      theme.showPoweredBy ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Przyciski akcji */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Zapisywanie...' : 'Zapisz zmiany'}
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Resetuj
            </button>
          </div>
        </div>

        {/* Podgląd */}
        <div className="lg:sticky lg:top-6">
          <div className="bg-gray-100 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-orange-500" />
              Podgląd
            </h3>
            
            {/* Miniatura czatu */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div 
                className="h-96 relative"
                style={{ backgroundColor: '#f3f4f6' }}
              >
                {/* Przycisk czatu */}
                <button
                  className={`absolute ${theme.position === 'bottom-right' ? 'right-6' : 'left-6'} bottom-6 text-white p-4 shadow-2xl transition-all`}
                  style={{
                    backgroundColor: theme.primaryColor,
                    borderRadius: borderRadiusMap[theme.borderRadius]
                  }}
                >
                  <MessageSquare className="w-6 h-6" />
                </button>

                {/* Widget czatu */}
                <div 
                  className={`absolute ${theme.position === 'bottom-right' ? 'right-6' : 'left-6'} bottom-24 w-80 shadow-2xl overflow-hidden`}
                  style={{
                    backgroundColor: theme.backgroundColor,
                    borderRadius: borderRadiusMap[theme.borderRadius]
                  }}
                >
                  {/* Header */}
                  <div 
                    className="p-4 text-white"
                    style={{
                      background: `linear-gradient(to right, ${theme.primaryColor}, ${theme.secondaryColor})`
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.2)'
                        }}
                      >
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{theme.companyName}</h3>
                        {theme.showPoweredBy && (
                          <p className="text-xs opacity-80">Powered by Claude AI</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-4">
                    <div className="bg-gray-800 rounded-lg p-3 text-sm text-gray-200">
                      {theme.welcomeMessage}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Wskazówka:</strong> Zmiany będą widoczne po zapisaniu i odświeżeniu widgetu czatu.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
